import { useCallback, useEffect, useState } from 'react'
import { Button, Card, Empty, Select, Space, Table, Tag, Typography, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../services/api'

const { Text } = Typography

interface AuditEvent {
    id: number
    event_type: string
    event_label: string
    actor_name: string | null
    actor_email?: string | null
    target_name: string | null
    old_label: string | null
    new_label: string | null
    description: string | null
    created_at: string
}

// Журнал системных действий. Историю по конкретному лиду смотрят в его
// карточке; сюда попадает то, у чего карточки нет, — прежде всего выдача ролей.
export default function AuditLog() {
    const [events, setEvents] = useState<AuditEvent[]>([])
    const [loading, setLoading] = useState(false)
    const [types, setTypes] = useState<{ value: string; label: string }[]>([])
    const [actors, setActors] = useState<{ value: string; label: string }[]>([])
    const [filter, setFilter] = useState<string | undefined>(undefined)
    const [actorFilter, setActorFilter] = useState<string | undefined>(undefined)

    const loadFilters = useCallback(() => {
        api.get<{
            types: { value: string; label: string }[]
            actors: { value: string; label: string }[]
        }>('/audit/types')
            .then((response) => {
                setTypes(response.data.types)
                setActors(response.data.actors)
            })
            .catch(() => undefined)
    }, [])

    useEffect(() => {
        loadFilters()
    }, [loadFilters])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await api.get<AuditEvent[]>('/audit', {
                params: {
                    ...(filter ? { event_type: filter } : {}),
                    ...(actorFilter ? { actor: actorFilter } : {}),
                },
            })
            setEvents(data)
        } catch (error) {
            const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
            message.error(detail || 'Не удалось загрузить журнал')
        } finally {
            setLoading(false)
        }
    }, [filter, actorFilter])

    useEffect(() => {
        load()
    }, [load])

    const columns = [
        {
            title: 'Когда',
            dataIndex: 'created_at',
            width: 170,
            render: (value: string) => dayjs(value).format('DD.MM.YYYY HH:mm:ss'),
        },
        {
            title: 'Событие',
            dataIndex: 'event_label',
            width: 180,
            render: (value: string) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: 'Кто',
            dataIndex: 'actor_name',
            width: 200,
            render: (value: string | null, row: AuditEvent) => value ? (
                <Space direction="vertical" size={0}>
                    <span>{value}</span>
                    {row.actor_email && <Text type="secondary" style={{ fontSize: 12 }}>{row.actor_email}</Text>}
                </Space>
            ) : <Text type="secondary">система</Text>,
        },
        {
            title: 'Что именно',
            key: 'change',
            render: (_: unknown, row: AuditEvent) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.description || '—'}</Text>
                    {row.event_type === 'role_changed' && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.old_label || '—'} → {row.new_label || '—'}
                        </Text>
                    )}
                </Space>
            ),
        },
    ]

    return (
        <Card
            title="Журнал действий"
            extra={(
                <Space wrap>
                    <Select
                        allowClear
                        placeholder="Все события"
                        style={{ width: 220 }}
                        value={filter}
                        onChange={setFilter}
                        options={types}
                    />
                    {/* Отдельный фильтр по сотруднику: список собирается из
                        самого журнала, поэтому в нём только те, кто что-то делал. */}
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Все сотрудники"
                        style={{ width: 220 }}
                        value={actorFilter}
                        onChange={setActorFilter}
                        options={actors}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => { load(); loadFilters() }}
                        loading={loading}
                    />
                </Space>
            )}
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', marginTop: 24 }}
        >
            <Text type="secondary" style={{ fontSize: 12 }}>
                Все подразделы настроек, выдача ролей, заведение, правка и удаление учётных
                записей. Действия над конкретным лидом — назначения, передачи, автораспределение —
                пишутся в историю его карточки и видны там.
            </Text>
            <Table
                rowKey="id"
                size="small"
                style={{ marginTop: 12 }}
                columns={columns}
                dataSource={events}
                loading={loading}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: <Empty description="Пока ничего не записано" /> }}
            />
        </Card>
    )
}
